"use client";

import { useEffect, useRef, useState } from "react";

import type { AuditionState, MidiAuditionPlayer } from "@/lib/midi/midi-audition-player";
import { LoaderIcon, PauseIcon, PlayIcon } from "../../atoms/icons";

import { DensitySparkline } from "./density-sparkline";

interface AuditionPlayerButtonProps {
  player: MidiAuditionPlayer | null;
  state: AuditionState;
  /** Window duration in seconds. Drives the timecode display. */
  durationSec: number;
  /** Global density buckets (60 entries recommended) for the back bar. */
  densityBuckets: readonly number[];
  /** Audition track indices currently checked — the live preview mix. */
  selectedTracks: readonly number[];
  /** Forwarded to the parent so it can fire `audition_global_played` (once). */
  onPlayed?: () => void;
}

const DEBOUNCE_MS = 200;
/** Delay before showing the `Chargement des sons…` label (spec §6.5). */
const LOADING_LABEL_DELAY_MS = 600;

function formatTimecode(seconds: number): string {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);

  return `${m}:${s.toString().padStart(2, "0")}`;
}

interface PlaybackProgress {
  isPlaying: boolean;
  elapsedSec: number;
  ratio: number; // [0, 1]
}

/**
 * Polls `AudioContext.currentTime` via `requestAnimationFrame` to derive
 * the playback elapsed-time for the timecode + scanning veil. Falls back
 * to wall-clock time when the player is not in `playing` state.
 */
const IDLE_PROGRESS: PlaybackProgress = { isPlaying: false, elapsedSec: 0, ratio: 0 };

function usePlaybackProgress(
  player: MidiAuditionPlayer | null,
  state: AuditionState,
  durationSec: number,
): PlaybackProgress {
  const [progress, setProgress] = useState<PlaybackProgress>(IDLE_PROGRESS);
  const isActive = state.kind === "playing" && Boolean(player);

  useEffect(() => {
    if (!isActive) {
      // Nothing to schedule, the consumer reads the idle fallback below.
      return;
    }

    let raf = 0;
    const wallStart = performance.now();

    const tick = () => {
      // `state.startedAt` is captured against `AudioContext.currentTime`;
      // wall-clock delta is a faithful proxy for the audio-clock elapsed
      // without having to expose the player's context.
      const elapsedSec = Math.max(0, (performance.now() - wallStart) / 1000);
      const ratio = durationSec > 0 ? Math.min(1, elapsedSec / durationSec) : 0;
      setProgress({ isPlaying: true, elapsedSec, ratio });
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      setProgress(IDLE_PROGRESS);
    };
  }, [isActive, durationSec]);

  return isActive ? progress : IDLE_PROGRESS;
}

export function AuditionPlayerButton({
  player,
  state,
  durationSec,
  densityBuckets,
  selectedTracks,
  onPlayed,
}: AuditionPlayerButtonProps) {
  const lastClickAt = useRef(0);
  const playedFiredRef = useRef(false);
  const [loadingLabelArmed, setLoadingLabelArmed] = useState(false);
  const progress = usePlaybackProgress(player, state, durationSec);

  // Deferred "Chargement des sons…" label. We arm the label only when the
  // loading state has lasted past the 600ms threshold; flipping back to a
  // non-loading state simply lets the next render hide the label without
  // forcing an extra setState (the derived `showLoadingLabel` below covers
  // it for free).
  useEffect(() => {
    if (state.kind !== "loading") {
      return;
    }

    const tid = setTimeout(() => {
      setLoadingLabelArmed(true);
    }, LOADING_LABEL_DELAY_MS);

    return () => {
      clearTimeout(tid);
      setLoadingLabelArmed(false);
    };
  }, [state.kind]);

  const showLoadingLabel = state.kind === "loading" && loadingLabelArmed;

  const isLoading = state.kind === "loading";
  const isError = state.kind === "error";
  const isPlaying = state.kind === "playing" && state.mode === "selection";
  // A solo track playing leaves the selection button effectively paused —
  // same disabled treatment as `loading`.
  const isOtherPlaying = state.kind === "playing" && state.mode !== "selection";
  const hasSelection = selectedTracks.length > 0;

  const handleClick = () => {
    if (!player) {
      return;
    }

    const now = performance.now();

    if (now - lastClickAt.current < DEBOUNCE_MS) {
      return;
    }

    lastClickAt.current = now;

    if (isPlaying) {
      player.stop();

      return;
    }

    if (!hasSelection) {
      return;
    }

    // Plays the checked tracks together over the whole song. (On error the
    // same call retries; preload is retried lazily on the next noteOn.)
    player.playSelection([...selectedTracks]);

    if (!playedFiredRef.current) {
      playedFiredRef.current = true;
      onPlayed?.();
    }
  };

  const label = resolveLabel(state, progress.elapsedSec, durationSec, showLoadingLabel, hasSelection);
  const disabled = isLoading || isOtherPlaying || !hasSelection || !player;

  return (
    <div
      className="relative w-full"
      style={{
        backgroundColor: "rgba(255,255,255,0.04)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: "var(--radius-card)",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          aria-label={isPlaying ? "Mettre en pause l'écoute" : "Écouter ma sélection"}
          className="shrink-0 flex items-center justify-center transition-all duration-150 cursor-pointer"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: resolveButtonBg(state),
            color: isPlaying ? "var(--color-bg-deep)" : "var(--color-accent-bright)",
            border: isError
              ? "1px solid rgba(251,191,36,0.4)"
              : "1px solid transparent",
            opacity: disabled ? 0.55 : 1,
          }}
        >
          {isLoading ? (
            <LoaderIcon size={16} className="animate-spin" />
          ) : isPlaying ? (
            <PauseIcon size={16} />
          ) : isError ? (
            <span style={{ fontSize: 14 }} aria-hidden>
              ⚠
            </span>
          ) : (
            <PlayIcon size={16} />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <p
            className="text-[13px] font-medium leading-tight"
            style={{
              color: isError ? "var(--color-accent-warm)" : "var(--color-text-primary)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {label}
          </p>
        </div>
      </div>

      {/* Density bar + scanning overlay */}
      <div className="relative" style={{ height: 20 }}>
        <DensitySparkline
          buckets={densityBuckets}
          width={640}
          height={20}
          variant="global"
          isActive={isPlaying}
          fluid
        />
        {isPlaying && progress.ratio > 0 && (
          <>
            {/* Scanning veil */}
            <div
              aria-hidden
              className="absolute top-0 bottom-0"
              style={{
                left: 0,
                width: `${progress.ratio * 100}%`,
                background:
                  "linear-gradient(90deg, rgba(139,92,246,0) 0%, rgba(139,92,246,0.18) 100%)",
                pointerEvents: "none",
              }}
            />
            {/* Cursor line */}
            <div
              aria-hidden
              className="absolute top-0 bottom-0"
              style={{
                left: `${progress.ratio * 100}%`,
                width: 1,
                backgroundColor: "var(--color-accent)",
                pointerEvents: "none",
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function resolveButtonBg(state: AuditionState): string {
  if (state.kind === "playing" && state.mode === "selection") {
    return "var(--color-accent)";
  }

  if (state.kind === "error") {
    return "rgba(251,191,36,0.08)";
  }

  return "var(--color-accent-subtle)";
}

function resolveLabel(
  state: AuditionState,
  elapsedSec: number,
  durationSec: number,
  showLoadingLabel: boolean,
  hasSelection: boolean,
): string {
  const total = formatTimecode(durationSec);

  if (state.kind === "loading") {
    return showLoadingLabel ? "Chargement des sons…" : "Préparation de l'écoute";
  }

  if (state.kind === "error") {
    return "Son indisponible — Réessayer";
  }

  if (state.kind === "playing" && state.mode === "selection") {
    return `En lecture · ${formatTimecode(elapsedSec)} / ${total}`;
  }

  if (!hasSelection) {
    return "Coche des pistes pour écouter";
  }

  return `Écouter ma sélection · 0:00 / ${total}`;
}
